import Shell from "app/components/Shell";
import Spinner from "app/components/Spinner";
import Signin from "app/features/authentication/signin";
import Home from "app/features/home";
import LessonPage from "app/features/lesson";
import Messages from "app/features/messages";
import NotFound from "app/features/not-found";
import Settings from "app/features/settings";
import Timetable from "app/features/timetable";
import { useLessonAlarms } from "app/native/useLessonAlarms";
import { sessionEvents } from "app/services/index";
import { useMeQuery } from "app/services/account";
import { MAIN_SELECTORS, userSlice } from "app/store";
import { identify } from "./instrument";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function RequireAuth() {
  const { signedIn } = useSelector(MAIN_SELECTORS.user);
  const location = useLocation();
  if (!signedIn) return <Navigate to="/login" replace state={{ redirectTarget: location.pathname }} />;
  return <Outlet />;
}

/** Runs once a parent is signed in: loads the family, identifies them for Sentry, keeps the alarms planned. */
function SignedInEffects() {
  const dispatch = useDispatch();
  const { data } = useMeQuery();
  useEffect(() => {
    if (data?.profile) { dispatch(userSlice.actions.signedIn(data.profile)); identify({ id: data.profile.parentId, email: data.profile.email, username: `${data.profile.name} ${data.profile.lastName}`.trim() }); }
  }, [data, dispatch]);
  useLessonAlarms(true);
  return null;
}

export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signedIn } = useSelector(MAIN_SELECTORS.user);

  useEffect(() => {
    const onOut = () => { dispatch(userSlice.actions.signedOut()); navigate("/login", { replace: true }); };
    sessionEvents.addEventListener("signed-out", onOut);
    return () => sessionEvents.removeEventListener("signed-out", onOut);
  }, [dispatch, navigate]);

  // A tapped notification (web push) tells the open page where to go.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => { if (e.data?.type === "classtutor-push" && e.data.kind === "message") { /* the Messages tab polls; the badge updates itself */ } };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  return (
    <>
      {signedIn && <SignedInEffects />}
      <SentryRoutes>
        <Route path="/login" element={signedIn ? <Navigate to="/" replace /> : <Signin />} />
        <Route element={<RequireAuth />}>
          <Route element={<Shell />}>
            <Route index element={<Home />} />
            <Route path="/timetable" element={<Timetable />} />
            <Route path="/messages" element={<Messages />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/lesson/:registerId" element={<LessonPage />} />
          </Route>
        </Route>
        <Route path="/loading" element={<Spinner />} />
        <Route path="*" element={<NotFound />} />
      </SentryRoutes>
    </>
  );
}
